import { useEffect, useMemo, useState } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import { useSearchParams } from "react-router";
import Drawer from "react-modern-drawer";
import Select from "react-select";
import { AsyncPaginate } from "react-select-async-paginate";
import {
    fetchJvByRestLink,
    fetchJvSlAccumulator,
    fetchReadJvClient,
    flattenReinsuranceTracker,
} from "../../utils/apiUtils";
import { loadClients } from "../../utils/optionLoaderUtils";
import { customStyles } from "../../utils/styleUtils";
import SharedTable from "../benefitPlan/SharedTable";
import { asJvArray } from "../benefitPlan/SharedTable/detailViews/StopLossContractDetail";
import { clientOptionFromSearchParams } from "./clientQuery";
import "./Reinsurance.scss";

const GUIDE_DRAWER_SIZE = 480;

const ACCUMULATOR_RESPONSE_KEYS = [
    "SLAccumulator",
    "SlAccumulator",
    "StopLossAccumulator",
    "SpecStopLossAccumulator",
    "ReinsuranceTracker",
    "Accumulator",
];

const CORE_COLUMNS = [
    "ClaimNumber",
    "FundType",
    "Age(Days)",
    "ClientResponsibleAmount",
    "ReinsuranceResponsibleAmount",
    "ReinsuranceOutstandingAmounts",
    "StopLossContractName",
    "StopLossContractID",
    "ClientID",
    "BlkOfBusID",
    "BlobID",
    "LocationName",
    "EmployeeID",
    "DependentID",
    "EffectiveDate",
    "SpecificAmount",
    "AmountAppliedToSpecDeductible",
    "PercentageOfSpecDeductibleMetAsOf",
    "SearchClmNumber",
    "AggregateContractName",
    "AggrContractID",
];

const SEARCH_MODES = [
    { value: "client", label: "Client" },
    { value: "employee", label: "Employee" },
    { value: "stopLossContract", label: "Stop-loss contract" },
    { value: "aggregateContract", label: "Aggregate contract" },
    { value: "claim", label: "Claim number" },
];

const MODE_HINTS = {
    client: "Required: Blob ID + Client ID + Effective Date. Location is added when the client has locations and you pick one.",
    employee: "Required: Employee ID + Effective Date. Add Dependent ID to search a specific dependent.",
    stopLossContract: "Required: Stop-loss contract name + Effective Date. Use the contract name (for example YOKES), not the contract ID.",
    aggregateContract: "Required: Aggregate contract name + Aggregate contract ID + Effective Date.",
    claim: "Required: Claim number + Effective Date.",
};

const ReinsuranceGuide = () => (
    <div className="jv-reinsurance__guide">
        <section aria-label="What this page is">
            <h3>What this page is</h3>
            <p>
                Javelina’s <strong>Search Reinsurance Tracker</strong> — the stop-loss
                accumulator as of a date. It answers “how much has been paid, applied to
                spec, filed, or is still outstanding under this contract / client /
                member?” It is not an HRP screen; it reads Javelina REST.
            </p>
        </section>

        <section aria-label="Specific vs aggregate">
            <h3>Specific vs aggregate</h3>
            <dl className="jv-reinsurance__defs">
                <div>
                    <dt>Specific stop-loss</dt>
                    <dd>
                        Protects the group when <strong>one person</strong> (certificate /
                        employee / dependent) exceeds the spec deductible. The tracker
                        shows paid claims in the contract period, amount applied to spec,
                        and what is unfiled, outstanding, or already reimbursed.
                    </dd>
                </div>
                <div>
                    <dt>Aggregate</dt>
                    <dd>
                        Protects the group when <strong>total</strong> claims exceed an
                        attachment point. Search with aggregate contract name <em>and</em>
                        aggregate contract ID.
                    </dd>
                </div>
            </dl>
        </section>

        <section aria-label="Effective date">
            <h3>Effective date</h3>
            <p>
                Every search includes an as-of date (defaults to today). The tracker
                is a snapshot: amounts and linked views are whatever Javelina has
                calculated through that date.
            </p>
        </section>

        <section aria-label="How to search">
            <h3>How to search</h3>
            <p>
                Javelina only accepts certain filter combinations. Pick a search
                mode; this page sends just those keys.
            </p>
            <dl className="jv-reinsurance__defs">
                <div>
                    <dt>Client</dt>
                    <dd>
                        <strong>Blob ID + Client ID + date</strong>. Blob is the block of
                        business from the client (or location). Location is optional when
                        the client has locations.
                    </dd>
                </div>
                <div>
                    <dt>Employee</dt>
                    <dd>
                        <strong>Employee ID + date</strong>. Add Dependent ID to pin a
                        specific dependent. No row means that member has no tracker
                        position as of the date.
                    </dd>
                </div>
                <div>
                    <dt>Stop-loss contract</dt>
                    <dd>
                        <strong>Contract name + date</strong> — the name (for example{" "}
                        <code>YOKES</code>), not the contract ID (<code>YOKES2023</code>).
                        Javelina rejects the ID with “Invalid Stop Loss Id”.
                    </dd>
                </div>
                <div>
                    <dt>Aggregate contract</dt>
                    <dd>
                        <strong>Aggregate contract name + aggregate contract ID + date</strong>.
                    </dd>
                </div>
                <div>
                    <dt>Claim number</dt>
                    <dd>
                        <strong>Claim number + date</strong>. The claim must be tied to a
                        specific stop-loss contract or Javelina returns a validation error.
                    </dd>
                </div>
            </dl>
        </section>

        <section aria-label="What the table shows">
            <h3>What the table shows</h3>
            <ul>
                <li>
                    <strong>Summary row</strong> — client (or similar) searches that have
                    no claim list. One row: position against spec, with linked views on Detail.
                </li>
                <li>
                    <strong>Claim list</strong> — contract searches return paginated
                    {" "}<code>Content</code> (typically 10 claims per page). Load more follows
                    Javelina’s next-page link until the last page.
                </li>
            </ul>
        </section>

        <section aria-label="Detail and linked views">
            <h3>Detail and linked views</h3>
            <p>
                Detail opens a dashboard: key amounts, facts, and grouped <strong>Open</strong>
                buttons for the Javelina views on that record (summaries, carrier-responsible
                claims, over-maximum, unfiled / Pay &amp; File, outstanding, reimbursed).
                Open loads the live record in a side drawer.
            </p>
            <p className="jv-reinsurance__guide-note">
                Filter views in the dashboard when a contract has many links. Raw URLs stay
                off the table — including More Columns.
            </p>
        </section>
    </div>
);

const inputStyle = {
    fontSize: "1rem",
    minWidth: "180px",
    padding: "1rem",
    borderRadius: ".4rem",
    border: "1px solid var(--lighterGrey)",
    outline: "none",
};

const pad2 = (n) => String(n).padStart(2, "0");

const todayIso = () => {
    const d = new Date();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const pick = (...vals) => vals.find((v) => v != null && String(v).trim() !== "");

const extractAccumulators = (payload) => {
    const response = payload?.Response || payload || {};
    const content = Array.isArray(response.Content)
        ? response.Content.filter((item) => item != null && typeof item === "object")
        : [];
    if (content.length) {
        const header = flattenReinsuranceTracker({ Response: response });
        return content.map((row) => ({
            ...header,
            ...flattenReinsuranceTracker({ Response: row }),
        }));
    }
    for (const key of ACCUMULATOR_RESPONSE_KEYS) {
        const rows = asJvArray(response[key]);
        if (rows.length) return rows.map((row) => flattenReinsuranceTracker({ Response: row }));
    }
    const { MetaData, Pagination, ...rest } = response;
    const leftover = Object.keys(rest).filter((k) => rest[k] != null && rest[k] !== "");
    if (!leftover.length) return [];
    if (response.SummaryAndAmounts || response.StopLossContractName) {
        return [flattenReinsuranceTracker(payload)];
    }
    return asJvArray(rest).length ? asJvArray(rest) : [rest];
};

const clientLocations = (readClientResult) => {
    const names = new Map();
    for (const client of asJvArray(readClientResult?.Response?.Client)) {
        for (const loc of asJvArray(client?.ClientLocation)) {
            const name = pick(loc?.LocationName);
            if (!name || names.has(name)) continue;
            names.set(name, loc);
        }
    }
    return [...names.values()];
};

const locationOptionsFrom = (locations) =>
    locations.map((loc) => ({
        value: loc.LocationName,
        label: loc.Description && loc.Description !== loc.LocationName
            ? `${loc.LocationName}: ${loc.Description}`
            : loc.LocationName,
        additional: loc,
    }));

const Reinsurance = () => {
    const [searchParams] = useSearchParams();
    const clientFromUrl = clientOptionFromSearchParams(searchParams);

    const [searchMode, setSearchMode] = useState(SEARCH_MODES[0]);
    const [guideOpen, setGuideOpen] = useState(false);
    const [selectedClient, setSelectedClient] = useState(() => clientFromUrl);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [employeeId, setEmployeeId] = useState("");
    const [dependentId, setDependentId] = useState("");
    const [stopLossContractName, setStopLossContractName] = useState("");
    const [aggregateContractName, setAggregateContractName] = useState("");
    const [aggrContractId, setAggrContractId] = useState("");
    const [searchClmNumber, setSearchClmNumber] = useState("");
    const [effectiveDate, setEffectiveDate] = useState(todayIso);
    const [debouncedText, setDebouncedText] = useState({
        employeeId: "",
        dependentId: "",
        stopLossContractName: "",
        aggregateContractName: "",
        aggrContractId: "",
        searchClmNumber: "",
    });
    const [showMoreColumns, setShowMoreColumns] = useState(false);
    const { ref, inView } = useInView();

    const mode = searchMode?.value;

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedText((prev) => {
                const next = {
                    employeeId,
                    dependentId,
                    stopLossContractName,
                    aggregateContractName,
                    aggrContractId,
                    searchClmNumber,
                };
                if (
                    prev.employeeId === next.employeeId
                    && prev.dependentId === next.dependentId
                    && prev.stopLossContractName === next.stopLossContractName
                    && prev.aggregateContractName === next.aggregateContractName
                    && prev.aggrContractId === next.aggrContractId
                    && prev.searchClmNumber === next.searchClmNumber
                ) {
                    return prev;
                }
                return next;
            });
        }, 400);
        return () => clearTimeout(timer);
    }, [employeeId, dependentId, stopLossContractName, aggregateContractName, aggrContractId, searchClmNumber]);

    const clientId = pick(
        selectedClient?.additional?.clientid,
        selectedClient?.additional?.ClientID,
        selectedClient?.value,
    );
    const clientBlobId = pick(
        selectedClient?.additional?.blkofbusid,
        selectedClient?.additional?.BlkOfBusID,
    );

    const clientDetailQuery = useQuery({
        queryKey: ["jv-reinsurance-client", clientId, clientBlobId],
        queryFn: () => fetchReadJvClient({ clientId, blkofbusid: clientBlobId }),
        enabled: mode === "client" && !!(clientId && clientBlobId),
        staleTime: 60_000,
        retry: false,
    });

    const locations = useMemo(
        () => clientLocations(clientDetailQuery.data),
        [clientDetailQuery.data],
    );
    const locationOptions = useMemo(() => locationOptionsFrom(locations), [locations]);
    const readClient = asJvArray(clientDetailQuery.data?.Response?.Client)[0] || {};

    useEffect(() => {
        setSelectedLocation(null);
    }, [clientId, clientBlobId]);

    useEffect(() => {
        if (locationOptions.length === 1) {
            setSelectedLocation(locationOptions[0]);
        }
    }, [clientId, clientBlobId, locationOptions]);

    const blobId = pick(
        selectedLocation?.additional?.BlkOfBusID,
        selectedLocation?.additional?.blkofbusid,
        readClient?.BlkOfBusID,
        clientBlobId,
    );

    const trackerParams = useMemo(() => {
        if (!effectiveDate) return null;

        if (mode === "client") {
            if (!clientId || !blobId) return null;
            const params = { BlobID: blobId, ClientID: clientId };
            if (selectedLocation?.value) params.LocationName = selectedLocation.value;
            params.EffectiveDate = effectiveDate;
            return params;
        }

        if (mode === "employee") {
            const id = debouncedText.employeeId.trim();
            if (!id) return null;
            const params = { EmployeeID: id };
            if (debouncedText.dependentId.trim()) params.DependentID = debouncedText.dependentId.trim();
            params.EffectiveDate = effectiveDate;
            return params;
        }

        if (mode === "stopLossContract") {
            const name = debouncedText.stopLossContractName.trim();
            if (!name) return null;
            return {
                StopLossContractName: name,
                EffectiveDate: effectiveDate,
            };
        }

        if (mode === "aggregateContract") {
            const name = debouncedText.aggregateContractName.trim();
            const id = debouncedText.aggrContractId.trim();
            if (!name || !id) return null;
            return {
                AggregateContractName: name,
                AggrContractID: id,
                EffectiveDate: effectiveDate,
            };
        }

        if (mode === "claim") {
            const claim = debouncedText.searchClmNumber.trim();
            if (!claim) return null;
            return { SearchClmNumber: claim, EffectiveDate: effectiveDate };
        }

        return null;
    }, [
        mode,
        clientId,
        blobId,
        selectedLocation?.value,
        debouncedText.employeeId,
        debouncedText.dependentId,
        debouncedText.stopLossContractName,
        debouncedText.aggregateContractName,
        debouncedText.aggrContractId,
        debouncedText.searchClmNumber,
        effectiveDate,
    ]);

    const canSearch = !!trackerParams;

    const {
        data,
        error,
        fetchNextPage,
        hasNextPage,
        isLoading,
        isFetching,
        isFetchingNextPage,
        status,
    } = useInfiniteQuery({
        queryKey: ["jv-reinsurance-tracker", trackerParams],
        queryFn: async ({ pageParam }) => {
            if (pageParam?.nextLink) {
                return await fetchJvByRestLink(pageParam.nextLink);
            }
            return await fetchJvSlAccumulator(trackerParams);
        },
        select: (data) => ({
            rows: data.pages.flatMap((page) => extractAccumulators(page)),
        }),
        initialPageParam: 0,
        getNextPageParam: (lastPage) => {
            const nextPageUrl = lastPage?.Response?.Pagination?.Next;
            if (!nextPageUrl) return undefined;
            return { nextLink: nextPageUrl };
        },
        staleTime: 0,
        retry: false,
        enabled: canSearch,
    });

    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) fetchNextPage();
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

    const columns = useMemo(() => {
        const rows = data?.rows || [];
        const isLinkColumn = (key, value) => (
            /link$/i.test(key)
            || (typeof value === "string" && (/\/rest\//i.test(value) || /^https?:\/\//i.test(value)))
        );
        if (showMoreColumns && rows[0]) {
            return Object.keys(rows[0]).filter((key) => {
                const value = rows[0][key];
                if (isLinkColumn(key, value)) return false;
                return value == null || ["string", "number", "boolean"].includes(typeof value);
            });
        }
        if (!rows[0]) return CORE_COLUMNS;
        return CORE_COLUMNS.filter((key) => rows.some((row) => row?.[key] != null && row[key] !== ""));
    }, [data?.rows, showMoreColumns]);

    const resetFilters = () => {
        setSelectedClient(null);
        setSelectedLocation(null);
        setEmployeeId("");
        setDependentId("");
        setStopLossContractName("");
        setAggregateContractName("");
        setAggrContractId("");
        setSearchClmNumber("");
        setDebouncedText({
            employeeId: "",
            dependentId: "",
            stopLossContractName: "",
            aggregateContractName: "",
            aggrContractId: "",
            searchClmNumber: "",
        });
        setEffectiveDate(todayIso());
    };

    const onModeChange = (option) => {
        setSearchMode(option);
        resetFilters();
    };

    return (
        <div className="container jv-reinsurance">
            <div className="jv-reinsurance__page-head">
                <div>
                    <p className="title">Reinsurance</p>
                    <p className="sub-title">
                        Search Reinsurance Tracker — stop-loss accumulators at contract / client / location / employee / dependent
                    </p>
                </div>
                <button
                    type="button"
                    className="jv-reinsurance__guide-toggle"
                    onClick={() => setGuideOpen((open) => !open)}
                    aria-pressed={guideOpen}
                    aria-expanded={guideOpen}
                >
                    {guideOpen ? "Hide guide" : "How reinsurance works"}
                </button>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "1rem 1rem 1rem 0rem" }}>
                <div style={{ display: "flex", gap: "1rem", alignItems: "center", width: "100%", flexWrap: "wrap" }}>
                    <Select
                        name="reinsuranceSearch"
                        className="react-select-container large-input"
                        classNamePrefix="react-select"
                        placeholder="Search by"
                        isClearable={false}
                        options={SEARCH_MODES}
                        value={searchMode}
                        onChange={onModeChange}
                    />

                    {mode === "client" && (
                        <>
                            <AsyncPaginate
                                placeholder="Client ID"
                                className="react-select-container large-input"
                                classNamePrefix="react-select"
                                debounceTimeout={300}
                                value={selectedClient}
                                clearCacheOnMenuClose={true}
                                clearCacheOnSearchChange={true}
                                isSearchable={true}
                                loadOptions={loadClients}
                                onChange={(option) => {
                                    setSelectedClient(option);
                                    setSelectedLocation(null);
                                }}
                                styles={customStyles}
                                isClearable={true}
                            />

                            <input
                                style={{ ...inputStyle, backgroundColor: "var(--lightestGrey)", color: "var(--grey)" }}
                                type="text"
                                readOnly
                                value={blobId || ""}
                                placeholder="Blob ID"
                                title="Block of business ID from the selected client (or location)"
                            />

                            {clientDetailQuery.isFetching && clientId ? (
                                <p style={{ color: "var(--grey)" }}>Loading locations…</p>
                            ) : locationOptions.length > 0 ? (
                                <Select
                                    className="react-select-container large-input"
                                    classNamePrefix="react-select"
                                    placeholder="Location name (optional)"
                                    isClearable={true}
                                    isSearchable={true}
                                    options={locationOptions}
                                    value={selectedLocation}
                                    onChange={setSelectedLocation}
                                    styles={customStyles}
                                />
                            ) : null}
                        </>
                    )}

                    {mode === "employee" && (
                        <>
                            <input
                                style={inputStyle}
                                type="text"
                                value={employeeId}
                                placeholder="Employee ID"
                                autoComplete="off"
                                onChange={(e) => setEmployeeId(e.target.value)}
                            />
                            <input
                                style={inputStyle}
                                type="text"
                                value={dependentId}
                                placeholder="Dependent ID (optional)"
                                autoComplete="off"
                                onChange={(e) => setDependentId(e.target.value)}
                            />
                        </>
                    )}

                    {mode === "stopLossContract" && (
                        <input
                            style={inputStyle}
                            type="text"
                            value={stopLossContractName}
                            placeholder="Stop-loss contract name"
                            autoComplete="off"
                            onChange={(e) => setStopLossContractName(e.target.value)}
                        />
                    )}

                    {mode === "aggregateContract" && (
                        <>
                            <input
                                style={inputStyle}
                                type="text"
                                value={aggregateContractName}
                                placeholder="Aggregate contract name"
                                autoComplete="off"
                                onChange={(e) => setAggregateContractName(e.target.value)}
                            />
                            <input
                                style={inputStyle}
                                type="text"
                                value={aggrContractId}
                                placeholder="Aggregate contract ID"
                                autoComplete="off"
                                onChange={(e) => setAggrContractId(e.target.value)}
                            />
                        </>
                    )}

                    {mode === "claim" && (
                        <input
                            style={inputStyle}
                            type="text"
                            value={searchClmNumber}
                            placeholder="Claim number"
                            autoComplete="off"
                            onChange={(e) => setSearchClmNumber(e.target.value)}
                        />
                    )}

                    <input
                        style={inputStyle}
                        type="date"
                        value={effectiveDate}
                        onChange={(e) => setEffectiveDate(e.target.value)}
                    />
                </div>
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0rem 1rem 1rem 0rem", gap: "1rem" }}>
                <p
                    style={{ cursor: "pointer", color: "var(--red)", flexShrink: 0 }}
                    onClick={resetFilters}
                >
                    Clear filters
                </p>
                <p style={{ color: "var(--grey)", fontSize: ".9rem" }}>
                    {MODE_HINTS[mode]}
                </p>
                <p style={{ cursor: "pointer", color: "var(--green)", flexShrink: 0 }} onClick={() => setShowMoreColumns(!showMoreColumns)}>
                    {showMoreColumns ? "Less Columns" : "More Columns"}
                </p>
            </div>

            {mode === "client" && clientDetailQuery.isError && (
                <p className="danger" style={{ paddingBottom: "1rem" }}>
                    Client details: {clientDetailQuery.error.message}
                </p>
            )}

            {status === "loading" ? (
                <p>Loading...</p>
            ) : status === "error" ? (
                <span className="danger">Error: {error.message}</span>
            ) : (
                <SharedTable
                    type={"jv-reinsurance"}
                    columns={columns}
                    disableHeaderFormatting={true}
                    items={data?.rows}
                    activeDraftId={null}
                    hasNextPage={hasNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                    fetchNextPage={fetchNextPage}
                    isFetching={isFetching}
                    isLoading={isLoading}
                    onDetailHandler={async (row) => row}
                    ref={ref}
                    noResultsMessage={
                        canSearch
                            ? "No reinsurance tracker rows for this filter combination."
                            : MODE_HINTS[mode]
                    }
                />
            )}

            <Drawer
                open={guideOpen}
                onClose={() => setGuideOpen(false)}
                direction="right"
                size={GUIDE_DRAWER_SIZE}
                duration={200}
                zIndex={220}
                className="jv-reinsurance__drawer"
                overlayClassName="jv-reinsurance__drawer-overlay"
            >
                <div className="jv-reinsurance__panel">
                    <header className="jv-reinsurance__panel-head">
                        <div>
                            <p className="jv-reinsurance__panel-kicker">Javelina</p>
                            <h2>How reinsurance works</h2>
                        </div>
                        <button
                            type="button"
                            className="jv-reinsurance__panel-close"
                            onClick={() => setGuideOpen(false)}
                        >
                            Close
                        </button>
                    </header>
                    <div className="jv-reinsurance__panel-body">
                        <ReinsuranceGuide />
                    </div>
                </div>
            </Drawer>
        </div>
    );
};

export default Reinsurance;
